import anime from 'animejs';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import InsigniaIcon from '@/components/molecules/InsigniaIcon';

interface Props {
  show: boolean;
  gender: string | null;
}

export default function GenderChangeModal({ show, gender }: Props) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLDivElement>(null);
  const dotRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const ringAnimRef = useRef<ReturnType<typeof anime> | null>(null);
  const dotAnimRef = useRef<ReturnType<typeof anime> | null>(null);
  const tlRef = useRef<ReturnType<typeof anime> | null>(null);

  useEffect(() => {
    if (!show) return;
    setVisible(true);
  }, [show]);

  useEffect(() => {
    if (!visible || !overlayRef.current || !cardRef.current || !iconRef.current) return;

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!prefersReduced) {
      const tl = anime.timeline({ easing: 'easeOutCubic' });
      tl.add({ targets: overlayRef.current, opacity: [0, 1], duration: 200 });
      tl.add(
        {
          targets: cardRef.current,
          scale: [0.85, 1],
          opacity: [0, 1],
          duration: 300,
          easing: 'easeOutBack',
        },
        '-=100',
      );
      tl.add(
        {
          targets: iconRef.current,
          scale: [0.5, 1],
          rotate: ['-15deg', '0deg'],
          duration: 400,
          easing: 'easeOutElastic(1, .6)',
        },
        '-=200',
      );
      tlRef.current = tl;

      const rings = cardRef.current.querySelectorAll<HTMLElement>('.modal-ring');
      if (rings.length) {
        ringAnimRef.current = anime({
          targets: Array.from(rings),
          scale: [0.4, 1.4],
          opacity: [0.5, 0],
          duration: 1500,
          loop: true,
          delay: anime.stagger(400),
          easing: 'easeOutQuad',
        });
      }

      const dots = dotRefs.current.filter(Boolean);
      if (dots.length) {
        dotAnimRef.current = anime({
          targets: dots,
          translateY: [0, -12, 0],
          duration: 600,
          loop: true,
          delay: anime.stagger(150),
          easing: 'easeInOutQuad',
        });
      }
    }

    return () => {
      tlRef.current?.pause();
      ringAnimRef.current?.pause();
      dotAnimRef.current?.pause();
    };
  }, [visible]);

  useEffect(() => {
    if (show || !visible) return;

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      setVisible(false);
      return;
    }

    if (!overlayRef.current || !cardRef.current) {
      setVisible(false);
      return;
    }

    ringAnimRef.current?.pause();
    dotAnimRef.current?.pause();

    const exitTl = anime.timeline({
      easing: 'easeInCubic',
      complete: () => setVisible(false),
    });
    exitTl.add({ targets: cardRef.current, scale: [1, 0.85], opacity: [1, 0], duration: 200 });
    exitTl.add({ targets: overlayRef.current, opacity: [1, 0], duration: 250 }, '-=50');
    tlRef.current = exitTl;
  }, [show, visible]);

  if (!visible) return null;

  const label =
    gender === 'homme'
      ? t('CV.GenderSelect.male')
      : gender === 'femme'
        ? t('CV.GenderSelect.female')
        : t('CV.GenderSelect.neutral');

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 backdrop-blur-sm opacity-0"
    >
      <div
        ref={cardRef}
        className="bg-white rounded-2xl shadow-2xl p-8 flex flex-col items-center gap-4 min-w-[280px] opacity-0"
      >
        <div className="relative w-20 h-20" ref={iconRef}>
          <div className="modal-ring absolute inset-0 rounded-full border-2 border-[#21c294] opacity-0" />
          <div className="modal-ring absolute inset-0 rounded-full border-2 border-[#21c294] opacity-0" />
          <div className="modal-ring absolute inset-0 rounded-full border-2 border-[#21c294] opacity-0" />
          <div className="absolute inset-0 flex items-center justify-center">
            <InsigniaIcon width={40} height={38} variant="on-light" animated={false} />
          </div>
        </div>

        <div className="text-center">
          <p className="text-sm text-gray-500">{t('CV.GenderSelect.label')}</p>
          <p className="text-lg font-bold text-[#254441] mt-1">{label}</p>
        </div>

        <div className="flex gap-1">
          <span
            ref={el => {
              dotRefs.current[0] = el;
            }}
            className="w-2 h-2 rounded-full bg-[#21c294]"
          />
          <span
            ref={el => {
              dotRefs.current[1] = el;
            }}
            className="w-2 h-2 rounded-full bg-[#21c294]"
          />
          <span
            ref={el => {
              dotRefs.current[2] = el;
            }}
            className="w-2 h-2 rounded-full bg-[#21c294]"
          />
        </div>
      </div>
    </div>
  );
}
